import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Users, ShieldCheck, Lightning, ArrowRight, Newspaper, InstagramLogo, FacebookLogo, TiktokLogo, XLogo, TelegramLogo, PinterestLogo, ThreadsLogo } from '@phosphor-icons/react';

const LandingPage = () => {
  const features = [
    {
      icon: Brain,
      title: 'AI Psychology Coach',
      description: 'Get instant guidance on FOMO, revenge trading, and emotional control from GPT-5.2 powered coaching.'
    },
    {
      icon: Newspaper,
      title: 'Crypto News',
      description: 'Stay informed with the latest crypto headlines from top sources like CoinDesk and CoinTelegraph.'
    },
    {
      icon: Users,
      title: 'Discipline Community',
      description: 'Connect with traders focused on discipline. Share insights and support each other.'
    },
    {
      icon: ShieldCheck,
      title: 'Risk Management',
      description: 'Get AI reminders about position sizing and risk when emotions run high.'
    },
    {
      icon: Lightning,
      title: 'Real-time Alerts',
      description: 'Notifications to prevent FOMO and revenge trading before you make costly mistakes.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1756093035138-7135b07084b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0b3BvZ3JhcGh5JTIwbGluZXN8ZW58MHx8fHwxNzc1MDk1NTc4fDA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/trader_reset_icon.png"
              alt="Trader Reset AI"
              className="w-40 h-40 mx-auto mb-6 rounded-2xl"
              data-testid="hero-app-icon"
            />
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase leading-none text-white mb-6">
              Master Your
              <span className="text-[#007AFF]"> Trading Psychology</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#8892B0] max-w-2xl mx-auto mb-6 tracking-wide">
              Stop FOMO. Prevent revenge trading. Control your emotions. 
              AI-powered coaching and community support for disciplined traders.
            </p>
            <div className="flex items-center justify-center gap-5 mb-10">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-instagram">
                <InstagramLogo size={28} weight="regular" />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-facebook">
                <FacebookLogo size={28} weight="regular" />
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-tiktok">
                <TiktokLogo size={28} weight="regular" />
              </a>
              <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-twitter">
                <XLogo size={28} weight="regular" />
              </a>
              <a href="https://t.me" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-telegram">
                <TelegramLogo size={28} weight="regular" />
              </a>
              <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-pinterest">
                <PinterestLogo size={28} weight="regular" />
              </a>
              <a href="https://threads.net" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-threads">
                <ThreadsLogo size={28} weight="regular" />
              </a>
              <a href="https://substack.com" target="_blank" rel="noopener noreferrer" className="text-[#8892B0] hover:text-white transition-colors" data-testid="social-substack">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/></svg>
              </a>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-all"
                data-testid="hero-cta"
              >
                Start Free <ArrowRight size={20} weight="bold" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight uppercase text-white mb-4">
              Everything You Need for Trading Discipline
            </h2>
            <p className="text-[#8892B0] text-lg max-w-2xl mx-auto">
              Tools and coaching designed specifically to help you maintain emotional control in the markets.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#131313] border border-white/5 p-8 hover:bg-[#1A1A1A] hover:border-white/20 transition-colors"
              >
                <feature.icon size={40} weight="duotone" className="text-[#007AFF] mb-4" />
                <h3 className="font-heading font-bold text-xl text-white uppercase tracking-tight mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#8892B0] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-6">
              Ready to Trade with Discipline?
            </h2>
            <p className="text-[#8892B0] text-lg mb-10">
              Join thousands of traders who've transformed their psychology with Trader Reset AI.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-12 py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-all text-lg"
              data-testid="cta-register"
            >
              Start Your Reset <ArrowRight size={24} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#131313] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Brain size={24} weight="duotone" className="text-[#007AFF]" />
              <span className="font-heading font-bold text-white uppercase">Trader Reset AI</span>
            </div>
            <p className="text-[#5C6B82] text-sm">
              © 2026 Trader Reset AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
